import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Send, ArrowRight, ArrowLeft, CheckCircle, BarChart2, MessageSquare, Wrench, TrendingUp, FileText, Zap, Lightbulb, Star } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import NivaAvatar from './NivaAvatar';
import SEOMeta from './SEOMeta';
import assessmentService from '../services/assessmentService';
import { trackRomiStarted, trackRomiCompleted } from '../services/analytics';

// ── Helpers ──────────────────────────────────────────────────────────────────

const genSessionId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);

const SCORE_CONFIG = {
  'Reactive':    { color: 'text-red-400',     badge: 'bg-red-900/40 text-red-300     border-red-700/50', glow: 'from-red-950/60 via-gray-950',     description: "Your operation is running on memory and instinct. Every week is a scramble, and it's only getting harder as your portfolio grows." },
  'Developing':  { color: 'text-orange-400',  badge: 'bg-orange-900/40 text-orange-300 border-orange-700/50', glow: 'from-orange-950/50 via-gray-950', description: "You have some habits in place, but gaps are costing you time and money. A few targeted changes will make a big difference." },
  'Managed':     { color: 'text-yellow-400',  badge: 'bg-yellow-900/40 text-yellow-300 border-yellow-700/50', glow: 'from-yellow-950/40 via-gray-950', description: "You're in control most of the time. The next step is systematising what's still manual, and freeing yourself from the day-to-day." },
  'Optimized':   { color: 'text-cyan-400',    badge: 'bg-cyan-900/40 text-cyan-300   border-cyan-700/50',  glow: 'from-cyan-950/50 via-gray-950',    description: "Your operation is data-driven and mostly automated. You're ahead of most landlords, and now it's about compounding those gains." },
  'Intelligent': { color: 'text-emerald-400', badge: 'bg-emerald-900/40 text-emerald-300 border-emerald-700/50', glow: 'from-emerald-950/50 via-gray-950', description: "Your portfolio runs like a business. You're in the top tier. Keep building and your operation will keep improving on its own." },
};

const DIMENSION_META = {
  tenant_communication:  { Icon: MessageSquare, label: 'Tenant Communication' },
  maintenance_management:{ Icon: Wrench,        label: 'Maintenance Management' },
  financial_operations:  { Icon: TrendingUp,    label: 'Financial Operations' },
  compliance_docs:       { Icon: FileText,      label: 'Compliance & Docs' },
  technology_adoption:   { Icon: Zap,           label: 'Technology Adoption' },
};

const parseRec = (text) => {
  for (const sep of [' — ', ' – ', ': ']) {
    const idx = text.indexOf(sep);
    if (idx > 10 && idx < 100) return { title: text.slice(0, idx), body: text.slice(idx + sep.length) };
  }
  const dot = text.indexOf('. ');
  if (dot > 10 && dot < 90) return { title: text.slice(0, dot + 1), body: text.slice(dot + 2) };
  return { title: null, body: text };
};

// ── Sub-components ────────────────────────────────────────────────────────────

const ThinkingDots = () => (
  <div className="flex items-center gap-1 py-0.5">
    {[0, 150, 300].map(d => (
      <div
        key={d}
        className="w-2 h-2 rounded-full bg-gray-500 animate-bounce"
        style={{ animationDelay: `${d}ms` }}
      />
    ))}
  </div>
);

const ScoreCounter = ({ target }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let frame;
    const start = performance.now();
    const duration = 1400;
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setCount(Math.floor(eased * target));
      if (t < 1) frame = requestAnimationFrame(tick);
      else setCount(target);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target]);
  return <>{count}</>;
};

const DimensionBar = ({ dimKey, value, max }) => {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setWidth(Math.round((value / max) * 100)), 150);
    return () => clearTimeout(t);
  }, [value, max]);
  const pct = Math.round((value / max) * 100);
  const barColor = pct >= 70 ? 'from-emerald-500 to-emerald-400'
    : pct >= 45 ? 'from-cyan-500 to-cyan-400'
    : pct >= 25 ? 'from-yellow-500 to-orange-400'
    : 'from-orange-500 to-red-500';
  const meta = DIMENSION_META[dimKey] || { Icon: BarChart2, label: dimKey };
  const { Icon, label } = meta;
  return (
    <div className="bg-gray-900/80 rounded-xl p-4 border border-gray-800 hover:border-gray-700 transition-colors">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gray-800 flex items-center justify-center flex-shrink-0">
            <Icon className="h-3.5 w-3.5 text-gray-400" />
          </div>
          <span className="text-gray-300 text-sm font-medium">{label}</span>
        </div>
        <div className="text-right">
          <span className={`text-lg font-bold ${barColor.includes('emerald') ? 'text-emerald-400' : barColor.includes('cyan') ? 'text-cyan-400' : barColor.includes('yellow') ? 'text-yellow-400' : 'text-orange-400'}`}>{value}</span>
          <span className="text-gray-600 text-sm">/{max}</span>
        </div>
      </div>
      <div className="h-2.5 bg-gray-800 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full bg-gradient-to-r ${barColor} transition-all duration-1000 ease-out`}
          style={{ width: `${width}%` }}
        />
      </div>
      <div className="flex justify-between mt-1.5">
        <span className="text-gray-600 text-xs">0</span>
        <span className="text-gray-600 text-xs">{pct}%</span>
        <span className="text-gray-600 text-xs">{max}</span>
      </div>
    </div>
  );
};

// ── Views ─────────────────────────────────────────────────────────────────────

const WelcomeView = ({ onStart, starting }) => (
  <div className="min-h-screen bg-gray-950 flex flex-col">
    {/* Minimal nav */}
    <div className="px-6 py-4 flex items-center justify-between border-b border-gray-900">
      <Link to="/" className="text-cyan-400 font-bold text-lg tracking-tight">
        nivasapro.ai
      </Link>
      <a
        href="/#lead-form"
        className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-cyan-400 transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Get Early Access
      </a>
    </div>

    <div className="flex-1 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-lg text-center">
        {/* Niva avatar */}
        <div className="flex justify-center mb-6">
          <div className="h-20 w-20 rounded-3xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-900/40">
            <span className="text-3xl font-bold text-white select-none">N</span>
          </div>
        </div>

        {/* Headline */}
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3 leading-tight">
          Where does your rental operation stand?
        </h1>
        <p className="text-gray-400 text-lg mb-1">
          Chat with Niva for 3 minutes and get your free{' '}
          <span className="text-cyan-400 font-semibold">NivasaPro ROMI Score™</span>.
        </p>
        <p className="text-gray-500 text-sm mb-2">
          Rental Operations Maturity Index · Powered by Niva
        </p>
        <p className="text-gray-600 text-sm mb-10">
          Discover exactly where your operations sit on the maturity curve, and what to do next.
        </p>

        {/* CTA */}
        <button
          onClick={onStart}
          disabled={starting}
          className="inline-flex items-center gap-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-60 text-white font-semibold px-8 py-4 rounded-xl transition-colors text-base w-full sm:w-auto justify-center"
        >
          {starting ? 'Starting…' : (
            <>Get My ROMI Score <ArrowRight className="h-4 w-4" /></>
          )}
        </button>

        <p className="text-gray-700 text-xs mt-5">
          Takes ~3 minutes · No signup required · Powered by Niva AI
        </p>

        {/* Social proof bullets */}
        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center text-sm text-gray-500">
          {['100% free', 'Personalised results', 'No spam'].map(t => (
            <div key={t} className="flex items-center gap-1.5">
              <CheckCircle className="h-4 w-4 text-cyan-700 flex-shrink-0" />
              <span>{t}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const AssistantMessage = ({ content }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
      strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
      ul: ({ children }) => <ul className="list-disc list-inside space-y-1 mt-1">{children}</ul>,
      li: ({ children }) => <li className="text-gray-200">{children}</li>,
      hr: () => <hr className="border-gray-700 my-3" />,
      h2: ({ children }) => <h2 className="text-base font-bold text-white mt-3 mb-1">{children}</h2>,
      h3: ({ children }) => <h3 className="text-sm font-bold text-white mt-2 mb-1">{children}</h3>,
    }}
  >
    {content}
  </ReactMarkdown>
);

const CompletionActions = ({ onViewResults }) => (
  <div className="bg-gray-900 border-t border-gray-800 p-4 flex-shrink-0">
    <div className="max-w-2xl mx-auto space-y-3">
      <p className="text-center text-gray-500 text-xs mb-3">Your assessment is complete</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          onClick={onViewResults}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold px-5 py-3 rounded-xl transition-colors text-sm"
        >
          <BarChart2 className="h-4 w-4" />
          See My Full Results
        </button>
        <Link
          to="/features"
          className="flex-1 inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-gray-200 font-semibold px-5 py-3 rounded-xl transition-colors text-sm"
        >
          Explore Nivasapro.ai <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  </div>
);

const STEPS = [
  { label: 'Background' },
  { label: 'Your Info' },
  { label: 'Time' },
  { label: 'Pain Points' },
  { label: 'Systems' },
];

const AssessmentProgress = ({ progress }) => {
  // progress is 0-100, each step = 20%
  const completedSteps = Math.floor(progress / 20);

  return (
    <div className="bg-gray-900 border-b border-gray-800 px-4 pt-3 pb-0 flex-shrink-0 sticky top-0 z-10">
      {/* Top row: branding + nav */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <NivaAvatar size="sm" />
          <span className="text-white text-sm font-medium">Niva · ROMI Score™</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-gray-500 text-xs tabular-nums">{progress}% complete</span>
          <a
            href="/#lead-form"
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-cyan-400 transition-colors"
          >
            <ArrowLeft className="h-3 w-3" />
            nivasapro.ai
          </a>
        </div>
      </div>

      {/* Step indicators */}
      <div className="flex items-end gap-0 pb-0">
        {STEPS.map((step, i) => {
          const done    = i < completedSteps;
          const current = i === completedSteps && progress < 100;
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-1.5 pb-3">
              {/* Step label */}
              <span className={`text-[10px] font-medium leading-none transition-colors duration-500 ${
                done    ? 'text-cyan-400' :
                current ? 'text-white' :
                          'text-gray-600'
              }`}>
                {step.label}
              </span>
              {/* Step dot */}
              <div className={`w-2 h-2 rounded-full transition-all duration-500 ${
                done    ? 'bg-cyan-400' :
                current ? 'bg-white ring-2 ring-white/30' :
                          'bg-gray-700'
              }`} />
              {/* Segment bar */}
              <div className="w-full h-1 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all duration-700 ease-out"
                  style={{ width: done ? '100%' : current ? '40%' : '0%' }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ChatView = ({ messages, input, onInput, onSend, loading, progress, isCompleted, onViewResults }) => {
  const endRef = useRef(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col">
      <AssessmentProgress progress={progress} />

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'items-start gap-2.5'}`}
          >
            {msg.role === 'assistant' && (
              <NivaAvatar size="sm" className="mt-1 flex-shrink-0" />
            )}
            <div
              className={
                msg.role === 'user'
                  ? 'bg-cyan-700 text-white rounded-2xl rounded-tr-sm px-4 py-3 max-w-[75%] text-sm leading-relaxed'
                  : 'bg-gray-800 text-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[80%] sm:max-w-lg text-sm leading-relaxed'
              }
            >
              {msg.role === 'assistant' ? <AssistantMessage content={msg.content} /> : msg.content}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex items-start gap-2.5">
            <NivaAvatar size="sm" className="mt-1 flex-shrink-0" />
            <div className="bg-gray-800 rounded-2xl rounded-tl-sm px-4 py-3">
              <ThinkingDots />
            </div>
          </div>
        )}

        <div ref={endRef} />
      </div>

      {/* Input or completion actions */}
      {isCompleted ? (
        <CompletionActions onViewResults={onViewResults} />
      ) : (
        <div className="bg-gray-900 border-t border-gray-800 p-4 flex-shrink-0">
          <div className="flex gap-2 max-w-2xl mx-auto">
            <input
              value={input}
              onChange={e => onInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Type your answer…"
              disabled={loading}
              className="flex-1 bg-gray-800 text-white placeholder-gray-600 rounded-xl px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-cyan-600 disabled:opacity-50 transition-colors"
            />
            <button
              onClick={onSend}
              disabled={loading || !input.trim()}
              className="bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white rounded-xl px-4 py-3 transition-colors flex-shrink-0"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ResultsView = ({ score, leadName }) => {
  if (!score) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center px-6">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-400 mx-auto" />
          <p className="text-gray-400 text-sm">Calculating your ROMI Score™…</p>
          <p className="text-gray-600 text-xs">This takes a few seconds</p>
        </div>
      </div>
    );
  }
  const cfg = SCORE_CONFIG[score.score_label] || SCORE_CONFIG['Managed'];

  return (
    <div className="min-h-screen bg-gray-950 overflow-y-auto">

      {/* Sticky nav */}
      <div className="px-6 py-4 flex items-center justify-between border-b border-gray-900 sticky top-0 bg-gray-950/95 backdrop-blur z-10">
        <Link to="/" className="text-cyan-400 font-bold text-lg tracking-tight">nivasapro.ai</Link>
        <a
          href="/#lead-form"
          className="text-xs text-gray-500 hover:text-cyan-400 transition-colors flex items-center gap-1"
        >
          <ArrowLeft className="h-3 w-3" /> Get Early Access
        </a>
      </div>

      {/* ── Hero: score ── */}
      <div className={`relative bg-gradient-to-b ${cfg.glow} to-gray-950 overflow-hidden`}>
        {/* Decorative radial glow */}
        <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full blur-3xl opacity-10 ${cfg.color.replace('text-', 'bg-')}`} />

        <div className="relative max-w-3xl mx-auto px-5 pt-12 pb-10 text-center">
          <p className="text-gray-500 text-sm tracking-wide mb-1">
            {leadName ? `${leadName}'s` : 'Your'} NivasaPro ROMI Score™
          </p>
          <p className="text-gray-600 text-xs mb-8">Rental Operations Maturity Index</p>

          {/* Big score */}
          <div className="relative inline-block">
            <div className={`text-9xl font-black leading-none ${cfg.color} drop-shadow-lg`}>
              <ScoreCounter target={score.total_score} />
            </div>
            <span className="text-4xl font-light text-gray-600 ml-1">/100</span>
          </div>

          {/* Tier badge */}
          <div className="mt-5 mb-4">
            <span className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold border ${cfg.badge}`}>
              <Star className="h-3.5 w-3.5" />
              {score.score_label}
            </span>
          </div>

          {/* Tier description */}
          <p className="text-gray-400 text-sm max-w-md mx-auto leading-relaxed mb-8">
            {cfg.description}
          </p>

          {/* Inline CTA — visible immediately */}
          <div className="inline-flex flex-col sm:flex-row items-center gap-3">
            <a
              href="/#lead-form"
              className="inline-flex items-center gap-2 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold px-7 py-3.5 rounded-xl transition-all hover:shadow-lg hover:shadow-cyan-900/40 text-sm"
            >
              Get Early Access <ArrowRight className="h-4 w-4" />
            </a>
            <span className="text-gray-600 text-xs">Free to start · No credit card needed</span>
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="max-w-3xl mx-auto px-5 pb-20 space-y-8 mt-10">

        {/* Score breakdown — 2-col grid */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <BarChart2 className="h-4 w-4 text-cyan-400" />
            <h3 className="text-white font-semibold">Score Breakdown</h3>
            <span className="text-gray-600 text-xs ml-auto">Each dimension scored out of 20</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <DimensionBar dimKey="tenant_communication"  value={score.tenant_communication}  max={20} />
            <DimensionBar dimKey="maintenance_management" value={score.maintenance_management} max={20} />
            <DimensionBar dimKey="financial_operations"   value={score.financial_operations}   max={20} />
            <DimensionBar dimKey="compliance_docs"        value={score.compliance_docs}        max={20} />
            <DimensionBar dimKey="technology_adoption"    value={score.technology_adoption}    max={20} />
          </div>
        </div>

        {/* Key insight */}
        <div className="relative bg-gradient-to-br from-cyan-950/50 to-blue-950/30 border border-cyan-800/40 rounded-2xl p-7 overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="flex items-start gap-3 relative">
            <div className="w-8 h-8 rounded-lg bg-cyan-900/60 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Lightbulb className="h-4 w-4 text-cyan-400" />
            </div>
            <div>
              <p className="text-cyan-400 text-xs font-bold uppercase tracking-wider mb-2">Key Insight</p>
              <p className="text-gray-100 text-base leading-relaxed">"{score.key_insight}"</p>
            </div>
          </div>
        </div>

        {/* Recommendations */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="h-4 w-4 text-cyan-400" />
            <h3 className="text-white font-semibold">How NivasaPro.ai Can Help</h3>
          </div>
          <div className="space-y-4">
            {score.top_recommendations.map((rec, i) => {
              const { title, body } = parseRec(rec);
              return (
                <div key={i} className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden hover:border-gray-700 transition-colors">
                  <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-800 bg-gray-900/80">
                    <div className="w-6 h-6 rounded-full bg-cyan-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                      {i + 1}
                    </div>
                    <p className="text-white text-sm font-semibold leading-snug">
                      {title || `Recommendation ${i + 1}`}
                    </p>
                  </div>
                  <div className="px-5 py-4">
                    <p className="text-gray-400 text-sm leading-relaxed">{body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA — bold and full-width */}
        <div className="relative bg-gradient-to-br from-cyan-950/80 via-blue-950/60 to-gray-900 border border-cyan-800/30 rounded-2xl overflow-hidden">
          {/* Decorative background blobs */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/5 rounded-full translate-x-1/3 -translate-y-1/3 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/5 rounded-full -translate-x-1/3 translate-y-1/3 pointer-events-none" />

          <div className="relative px-8 py-10 sm:px-12 text-center">
            {/* Logo mark */}
            <div className="flex justify-center mb-5">
              <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-xl shadow-cyan-900/50">
                <span className="text-2xl font-black text-white select-none">N</span>
              </div>
            </div>

            <h3 className="text-white text-2xl sm:text-3xl font-bold mb-3 leading-tight">
              Ready to move from <span className={cfg.color}>{score.score_label}</span> to Intelligent?
            </h3>
            <p className="text-gray-400 text-sm sm:text-base mb-8 max-w-lg mx-auto leading-relaxed">
              NivasaPro.ai gives you Niva, an AI agent that handles maintenance, tenant communication,
              and rent tracking automatically. Your portfolio, without the grind.
            </p>

            {/* Trust signals */}
            <div className="flex flex-wrap justify-center gap-4 mb-8 text-xs text-gray-500">
              {['Free to start', 'No credit card required', 'Set up in minutes', 'Cancel anytime'].map(t => (
                <span key={t} className="flex items-center gap-1.5">
                  <CheckCircle className="h-3 w-3 text-cyan-600" /> {t}
                </span>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="/#lead-form"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-cyan-600 hover:bg-cyan-500 text-white font-bold px-10 py-4 rounded-xl transition-all hover:shadow-xl hover:shadow-cyan-900/50 text-base"
              >
                Get Early Access <ArrowRight className="h-5 w-5" />
              </a>
              <Link
                to="/"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-gray-700 hover:border-gray-500 text-gray-300 hover:text-white font-semibold px-8 py-4 rounded-xl transition-colors text-base"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const LandlordAssessment = () => {
  const [view, setView]         = useState('welcome');
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [sessionId]             = useState(() => genSessionId());
  const [assessState, setAssessState] = useState(null);
  const [transcript, setTranscript]   = useState([]);
  const [score, setScore]           = useState(null);
  const [progress, setProgress]     = useState(0);
  const [leadName, setLeadName]     = useState('');
  const [isCompleted, setIsCompleted] = useState(false);

  const handleStart = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await assessmentService.start(sessionId);
      trackRomiStarted();
      setMessages([{ role: 'assistant', content: result.response }]);
      setTranscript([{ role: 'assistant', content: result.response }]);
      setAssessState(result.state);
      setProgress(result.progress?.percentage ?? 0);
      setView('chat');
    } catch (e) {
      setError('Could not connect to Niva. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);

    try {
      const result = await assessmentService.chat(
        sessionId,
        text,
        assessState,
        transcript,
      );

      // Update transcript AFTER the call (prior history contract)
      const updatedTranscript = [
        ...transcript,
        { role: 'user', content: text },
        { role: 'assistant', content: result.response },
      ];
      setTranscript(updatedTranscript);
      setMessages(prev => [...prev, { role: 'assistant', content: result.response }]);
      setAssessState(result.state);

      const pct = result.progress?.percentage ?? 0;
      setProgress(pct);
      console.log('[Assessment] progress:', pct, '| phases_completed:', result.state?.phases_completed, '| is_completed:', result.is_completed);

      if (result.is_completed) {
        if (result.score) {
          setScore(result.score);
          trackRomiCompleted(result.score.total_score, result.score.score_label);
        }
        setLeadName(result.state?.lead_name || '');
        setIsCompleted(true);
      }
    } catch (e) {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: "Sorry, I had a connection issue. Could you send that again?" },
      ]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, sessionId, assessState, transcript]);

  if (view === 'welcome') {
    return (
      <>
        {/* Welcome is the state that gets pre-rendered and crawled. Without an
            explicit canonical here the snapshot inherits the fallback shell's
            homepage meta, and Search Console files this URL as a duplicate of "/". */}
        <SEOMeta
          title="Take the ROMI Score™ Assessment"
          description="Answer a few questions and get your free ROMI Score™, an AI assessment of where your rental operation stands, with specific next steps to improve it."
          canonical="/niva-romi-score"
        />
        <WelcomeView onStart={handleStart} starting={loading} />
        {error && (
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-red-900 text-red-200 text-sm px-4 py-2 rounded-lg shadow-lg">
            {error}
          </div>
        )}
      </>
    );
  }

  if (view === 'results') {
    return <ResultsView score={score} leadName={leadName} />;
  }

  return (
    <ChatView
      messages={messages}
      input={input}
      onInput={setInput}
      onSend={handleSend}
      loading={loading}
      progress={progress}
      isCompleted={isCompleted}
      onViewResults={() => setView('results')}
    />
  );
};

export default LandlordAssessment;
